import { ApiBase } from '@bymason/api-base';
import {
  CreateProjectOptions,
  CreateProjectResponse,
  ListProjectsOptions,
  ListProjectsResponse,
  ReadProjectOptions,
  ReadProjectResponse,
} from './types';

export * from './types';

class Projects extends ApiBase {
  private static instance: Projects;

  private constructor() {
    super();
  }

  /**
   * There is no need for consumers to ever call this method as its purely used to enforce a singleton module pattern.
   * @returns A Projects instance
   */
  public static getInstance(): Projects {
    if (!Projects.instance) {
      Projects.instance = new Projects();
    }

    return Projects.instance;
  }

  /**
   * Creates a project.
   * @param options model data required to create a project.
   * @param clientToken token required for proper authorization.
   * @returns either a Project model or void
   */
  public async createProject(options: CreateProjectOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('projects');
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<CreateProjectResponse>({
      body: JSON.stringify(options),
      clientToken,
      method: 'POST',
      url,
    });

    return dataResponse;
  }

  /**
   * Gets a project.
   * @param options metadata required to get a project.
   * @param clientToken token required for proper authorization.
   * @returns either a project model or void
   */
  public async readProject(options: ReadProjectOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('projects/:projectId');
    const url = this.expandUrlParams(urlTemplate, options);

    const dataResponse = await this.callJsonEndpoint<ReadProjectResponse>({
      clientToken,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  /**
   * Gets projects list.
   * @param options metadata required to get projects.
   * @param clientToken token required for proper authorization.
   * @returns either void or a pageable structure that includes projects.
   */
  public async listProjects(options: ListProjectsOptions, clientToken: string) {
    const urlTemplate = this.getEndpointTemplate('projects');
    const url = this.expandUrlParams(urlTemplate, options, { addUnusedParamsToSearch: true });

    const dataResponse = await this.callJsonEndpoint<ListProjectsResponse>({
      clientToken,
      errorOn404: false,
      method: 'GET',
      url,
    });

    return dataResponse;
  }
}

export const projectsClient = Projects.getInstance();
